import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { accountsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  ChevronRight,
  ChevronDown,
  FileText,
  X,
  Info,
  Calendar,
  Building2,
  Wallet,
  CreditCard,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Scale,
  PiggyBank,
  Landmark,
  ShoppingCart,
  Briefcase,
  HelpCircle,
  Edit2,
  Trash2,
} from 'lucide-react';

// Account Type Configuration (QuickBooks-style)
const ACCOUNT_TYPES = {
  asset: {
    label: 'Asset',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: Wallet,
    codeStart: 1000,
    detailTypes: [
      { value: 'bank', label: 'Bank', description: 'Checking, savings, money market accounts' },
      { value: 'accounts_receivable', label: 'Accounts Receivable', description: 'Money owed to you' },
      { value: 'other_current_asset', label: 'Other Current Asset', description: 'Short-term assets' },
      { value: 'fixed_asset', label: 'Fixed Asset', description: 'Long-term tangible assets' },
      { value: 'other_asset', label: 'Other Asset', description: 'Non-current assets' },
      { value: 'cash_and_equivalents', label: 'Cash and Cash Equivalents', description: 'Cash, petty cash, etc.' },
    ],
  },
  liability: {
    label: 'Liability',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: CreditCard,
    codeStart: 2000,
    detailTypes: [
      { value: 'credit_card', label: 'Credit Card', description: 'Credit card accounts' },
      { value: 'accounts_payable', label: 'Accounts Payable', description: 'Money you owe' },
      { value: 'other_current_liability', label: 'Other Current Liability', description: 'Short-term debts' },
      { value: 'long_term_liability', label: 'Long Term Liability', description: 'Long-term debts' },
    ],
  },
  equity: {
    label: 'Equity',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    icon: Scale,
    codeStart: 3000,
    detailTypes: [
      { value: 'owners_equity', label: "Owner's Equity", description: 'Owner investment' },
      { value: 'retained_earnings', label: 'Retained Earnings', description: 'Accumulated profits' },
      { value: 'capital_stock', label: 'Capital Stock', description: 'Shareholder equity' },
      { value: 'opening_balance_equity', label: 'Opening Balance Equity', description: 'Initial equity' },
    ],
  },
  revenue: {
    label: 'Revenue',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: TrendingUp,
    codeStart: 4000,
    detailTypes: [
      { value: 'income', label: 'Income', description: 'Sales and service income' },
      { value: 'other_income', label: 'Other Income', description: 'Interest, dividends, etc.' },
      { value: 'service_revenue', label: 'Service Revenue', description: 'Service-based income' },
      { value: 'sales_revenue', label: 'Sales Revenue', description: 'Product sales' },
    ],
  },
  expense: {
    label: 'Expense',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: TrendingDown,
    codeStart: 5000,
    detailTypes: [
      { value: 'operating_expense', label: 'Operating Expense', description: 'Day-to-day expenses' },
      { value: 'cost_of_goods_sold', label: 'Cost of Goods Sold', description: 'Direct costs' },
      { value: 'other_expense', label: 'Other Expense', description: 'Non-operating expenses' },
      { value: 'payroll_expense', label: 'Payroll Expense', description: 'Salaries and wages' },
    ],
  },
};

const CURRENCIES = [
  { value: 'USD', label: 'USD - United States Dollar' },
  { value: 'LRD', label: 'LRD - Liberian Dollar' },
];

// Spells out the currency code ("USD $1,500.00" / "LRD $10,000") instead of
// relying on "$" vs "L$" alone to distinguish them -- used everywhere an
// amount appears on this page so it's never ambiguous which currency
// you're looking at.
const formatLabeledCurrency = (value, currency = 'USD') => {
  const amount = currency === 'LRD'
    ? new Intl.NumberFormat('en-US').format(value || 0)
    : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
  return `${currency} $${amount}`;
};

// Renders a { USD, LRD } total as separate lines -- USD and LRD are
// different currencies, not interchangeable units, so they're never
// blended into one number. Both lines always show, even at zero, so
// every card/section total looks the same regardless of account type.
function CurrencyTotals({ totals, className = '' }) {
  return (
    <div className={className}>
      <p className="font-mono">{formatLabeledCurrency(totals?.USD, 'USD')}</p>
      <p className="font-mono text-xs opacity-80">{formatLabeledCurrency(totals?.LRD, 'LRD')}</p>
    </div>
  );
}

// Account Row Component
function AccountRow({ account, accounts, level = 0, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(true);
  const typeConfig = ACCOUNT_TYPES[account.type] || ACCOUNT_TYPES.asset;
  const Icon = typeConfig.icon;
  
  // Find child accounts
  const children = accounts.filter(a => a.parent_account_id === account.id);
  const hasChildren = children.length > 0;

  return (
    <>
      <tr className="border-b border-border/50 hover:bg-muted/30 group">
        <td className="py-3 px-3">
          <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 20}px` }}>
            {hasChildren ? (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-0.5 hover:bg-muted rounded"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <span className="font-mono text-sm font-medium">{account.code}</span>
          </div>
        </td>
        <td className="py-3 px-3">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${typeConfig.color}`} />
            <span className={level > 0 ? 'text-sm' : 'font-medium'}>{account.name}</span>
            {account.is_subaccount && (
              <span className="text-xs text-muted-foreground">(sub)</span>
            )}
          </div>
        </td>
        <td className="py-3 px-3">
          <span className={`px-2 py-0.5 text-xs rounded-full ${typeConfig.bgColor} ${typeConfig.color}`}>
            {typeConfig.label}
          </span>
        </td>
        <td className="py-3 px-3 text-sm text-muted-foreground capitalize">
          {account.detail_type?.replace(/_/g, ' ') || '-'}
        </td>
        <td className="py-3 px-3">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            account.currency === 'LRD' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
          }`}>
            {account.currency || 'USD'}
          </span>
        </td>
        <td className="py-3 px-3 text-right font-mono">
          <span className={account.balance < 0 ? 'text-red-600' : ''}>
            {formatLabeledCurrency(account.balance, account.currency || 'USD')}
          </span>
        </td>
        <td className="py-3 px-3 text-sm text-muted-foreground hidden lg:table-cell">
          {account.description?.substring(0, 50) || '-'}
          {account.description?.length > 50 && '...'}
        </td>
        <td className="py-3 px-2">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
            <button
              onClick={() => onEdit(account)}
              className="p-1 hover:bg-muted rounded"
              title="Edit"
              data-testid={`edit-account-${account.code}`}
            >
              <Edit2 className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button
              onClick={() => onDelete(account)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete"
              data-testid={`delete-account-${account.code}`}
            >
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </button>
          </div>
        </td>
      </tr>
      {expanded && children.map(child => (
        <AccountRow
          key={child.id}
          account={child}
          accounts={accounts}
          level={level + 1}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}

// New Account Form Modal
function NewAccountModal({ isOpen, onClose, accounts, companyId, editAccount }) {
  const queryClient = useQueryClient();
  
  // Generate next available code
  const generateCode = (type, currentAccounts = accounts) => {
    const typeConfig = ACCOUNT_TYPES[type];
    const typeAccounts = currentAccounts.filter(a => a.type === type);
    const maxCode = typeAccounts.reduce((max, a) => {
      const num = parseInt(a.code) || 0;
      return num > max ? num : max;
    }, typeConfig.codeStart - 10);
    return String(maxCode + 10);
  };

  // Compute initial form data based on whether editing or creating
  const getInitialFormData = () => {
    if (editAccount) {
      return {
        name: editAccount.name || '',
        type: editAccount.type || 'asset',
        detail_type: editAccount.detail_type || ACCOUNT_TYPES[editAccount.type || 'asset'].detailTypes[0].value,
        is_subaccount: editAccount.is_subaccount || false,
        parent_account_id: editAccount.parent_account_id || '',
        opening_balance: editAccount.opening_balance || '',
        opening_balance_date: editAccount.opening_balance_date || new Date().toISOString().split('T')[0],
        currency: editAccount.currency || 'USD',
        description: editAccount.description || '',
        code: editAccount.code || '',
      };
    }
    return {
      name: '',
      type: 'asset',
      detail_type: 'bank',
      is_subaccount: false,
      parent_account_id: '',
      opening_balance: '',
      opening_balance_date: new Date().toISOString().split('T')[0],
      currency: 'USD',
      description: '',
      code: generateCode('asset', accounts),
    };
  };
  
  // Initialize state with computed value - key prop on modal will reset this
  const [formData, setFormData] = useState(getInitialFormData);

  // Update code when type changes
  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      type,
      detail_type: ACCOUNT_TYPES[type].detailTypes[0].value,
      code: editAccount ? prev.code : generateCode(type),
    }));
  };

  // Get potential parent accounts (same type)
  const parentOptions = accounts.filter(
    a => a.type === formData.type && !a.parent_account_id && a.id !== editAccount?.id
  );

  const createMutation = useMutation({
    mutationFn: (data) => accountsAPI.create({ ...data, company_id: companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['accounts']);
      toast.success('Account created successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => accountsAPI.update(editAccount.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['accounts']);
      toast.success('Account updated successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Account name is required');
      return;
    }

    const submitData = {
      ...formData,
      code: formData.code || generateCode(formData.type),
      opening_balance: parseFloat(formData.opening_balance) || 0,
      balance: parseFloat(formData.opening_balance) || 0,
      parent_account_id: formData.is_subaccount ? formData.parent_account_id : null,
    };

    if (editAccount) {
      // Only send updateable fields
      const updateData = {
        name: submitData.name,
        detail_type: submitData.detail_type,
        is_subaccount: submitData.is_subaccount,
        parent_account_id: submitData.parent_account_id,
        currency: submitData.currency,
        description: submitData.description,
      };
      updateMutation.mutate(updateData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  if (!isOpen) return null;

  const currentTypeConfig = ACCOUNT_TYPES[formData.type];
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">
            {editAccount ? 'Edit Account' : 'New Account'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Account Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Account name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Cash and cash equivalents"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="account-name-input"
            />
          </div>

          {/* Account Type & Detail Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Account type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="account-type-select"
              >
                {Object.entries(ACCOUNT_TYPES).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Detail type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.detail_type}
                onChange={(e) => setFormData({ ...formData, detail_type: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="detail-type-select"
              >
                {currentTypeConfig.detailTypes.map((dt) => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {currentTypeConfig.detailTypes.find(dt => dt.value === formData.detail_type)?.description}
              </p>
            </div>
          </div>

          {/* Subaccount */}
          <div className="border border-border rounded-lg p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_subaccount}
                onChange={(e) => setFormData({ ...formData, is_subaccount: e.target.checked, parent_account_id: '' })}
                className="rounded border-border"
              />
              <span className="text-sm font-medium">Make this a subaccount</span>
            </label>
            
            {formData.is_subaccount && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1">
                  Parent account <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.parent_account_id}
                  onChange={(e) => setFormData({ ...formData, parent_account_id: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  required={formData.is_subaccount}
                  data-testid="parent-account-select"
                >
                  <option value="">Select parent account...</option>
                  {parentOptions.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} - {acc.currency}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Opening Balance & Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1 text-sm font-medium mb-1">
                Opening balance
                <button type="button" className="text-muted-foreground hover:text-foreground">
                  <Info className="h-3.5 w-3.5" />
                </button>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={formData.opening_balance}
                  onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                  placeholder="0.00"
                  className="flex-1 px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="opening-balance-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">As of</label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.opening_balance_date}
                  onChange={(e) => setFormData({ ...formData, opening_balance_date: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium mb-1">Currency</label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="currency-select"
            >
              {CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description for this account..."
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Account Code Preview */}
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Account Code: <span className="font-mono font-medium text-foreground">{formData.code || generateCode(formData.type)}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Auto-generated based on account type. {currentTypeConfig.label}s start at {currentTypeConfig.codeStart}.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <button
              type="button"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <HelpCircle className="h-4 w-4" />
              Video tutorials
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                data-testid="save-account-button"
              >
                {isPending ? 'Saving...' : (editAccount ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Chart of Accounts Page
export default function ChartOfAccounts() {
  const { selectedCompany } = useCompany();
  const [showModal, setShowModal] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedTypes, setExpandedTypes] = useState(Object.keys(ACCOUNT_TYPES));

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['accounts', selectedCompany?.id],
    queryFn: () => accountsAPI.getAll(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
  });

  // Filter accounts
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      const matchesSearch = 
        acc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        acc.code?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || acc.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [accounts, searchTerm, filterType]);

  // Sum a list of accounts' balances per currency instead of blending USD
  // and LRD into one number -- they're different currencies, not the same
  // unit, so adding them together produces a meaningless total.
  const sumByCurrency = (list) => {
    return list.reduce((totals, a) => {
      const currency = a.currency === 'LRD' ? 'LRD' : 'USD';
      totals[currency] += a.balance || 0;
      return totals;
    }, { USD: 0, LRD: 0 });
  };

  // Group accounts by type
  const groupedAccounts = useMemo(() => {
    return Object.entries(ACCOUNT_TYPES).map(([type, config]) => {
      const typeAccounts = filteredAccounts.filter(a => a.type === type && !a.parent_account_id);
      const allTypeAccounts = filteredAccounts.filter(a => a.type === type);
      const total = sumByCurrency(allTypeAccounts);
      return { type, config, accounts: typeAccounts, allAccounts: allTypeAccounts, total };
    });
  }, [filteredAccounts]);

  // Summary totals
  const summary = useMemo(() => {
    const assets = sumByCurrency(accounts.filter(a => a.type === 'asset'));
    const liabilities = sumByCurrency(accounts.filter(a => a.type === 'liability'));
    const equity = sumByCurrency(accounts.filter(a => a.type === 'equity'));
    const revenue = sumByCurrency(accounts.filter(a => a.type === 'revenue'));
    const expenses = sumByCurrency(accounts.filter(a => a.type === 'expense'));
    const netIncome = { USD: revenue.USD - expenses.USD, LRD: revenue.LRD - expenses.LRD };
    return { assets, liabilities, equity, revenue, expenses, netIncome };
  }, [accounts]);

  const toggleType = (type) => {
    setExpandedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleEdit = (account) => {
    setEditAccount(account);
    setShowModal(true);
  };

  const queryClient = useQueryClient();
  
  const deleteMutation = useMutation({
    mutationFn: (accountId) => accountsAPI.delete(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries(['accounts']);
      toast.success('Account deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    },
  });

  const handleDelete = (account) => {
    if (window.confirm(`Are you sure you want to delete "${account.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(account.id);
    }
  };

  return (
    <div className="space-y-6" data-testid="chart-of-accounts-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Chart of Accounts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage accounts for {selectedCompany?.name || 'your company'}
          </p>
        </div>
        <button
          onClick={() => { setEditAccount(null); setShowModal(true); }}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="add-account-button"
        >
          <Plus className="h-4 w-4" />
          New Account
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        {Object.entries(ACCOUNT_TYPES).map(([type, config]) => {
          const Icon = config.icon;
          const total = groupedAccounts.find(g => g.type === type)?.total || { USD: 0, LRD: 0 };
          return (
            <div key={type} className={`p-3 rounded-lg border ${config.borderColor} ${config.bgColor}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${config.color}`} />
                <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
              </div>
              <CurrencyTotals totals={total} className={`text-lg font-bold ${config.color}`} />
            </div>
          );
        })}
        <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-primary" />
            <span className="text-xs font-medium text-primary">Net Income</span>
          </div>
          <CurrencyTotals
            totals={summary.netIncome}
            className={`text-lg font-bold ${summary.netIncome.USD >= 0 ? 'text-green-600' : 'text-red-600'}`}
          />
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            data-testid="search-accounts"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-border rounded-lg bg-background"
        >
          <option value="all">All Types</option>
          {Object.entries(ACCOUNT_TYPES).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Accounts Table - Grouped by Type */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse">
              <div className="h-6 w-32 bg-muted rounded mb-4"></div>
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedAccounts.map((group) => {
            const Icon = group.config.icon;
            return (
              <div key={group.type} className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Group Header */}
                <button
                  onClick={() => toggleType(group.type)}
                  className={`w-full flex items-center justify-between p-4 hover:bg-muted/50 ${group.config.bgColor}`}
                >
                  <div className="flex items-center gap-3">
                    {expandedTypes.includes(group.type) ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                    <Icon className={`h-5 w-5 ${group.config.color}`} />
                    <span className={`font-semibold ${group.config.color}`}>{group.config.label}</span>
                    <span className="text-sm text-muted-foreground">
                      ({group.allAccounts.length} account{group.allAccounts.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <CurrencyTotals totals={group.total} className={`text-right font-semibold ${group.config.color}`} />
                </button>

                {/* Accounts Table */}
                {expandedTypes.includes(group.type) && group.accounts.length > 0 && (
                  <div className="border-t border-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/30 border-b border-border">
                          <th className="text-left py-2 px-3 font-medium w-28">Code</th>
                          <th className="text-left py-2 px-3 font-medium">Account Name</th>
                          <th className="text-left py-2 px-3 font-medium w-24">Type</th>
                          <th className="text-left py-2 px-3 font-medium w-36">Detail Type</th>
                          <th className="text-left py-2 px-3 font-medium w-20">Currency</th>
                          <th className="text-right py-2 px-3 font-medium w-32">Balance</th>
                          <th className="text-left py-2 px-3 font-medium hidden lg:table-cell">Description</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.accounts.map((account) => (
                          <AccountRow
                            key={account.id}
                            account={account}
                            accounts={filteredAccounts}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Empty State */}
                {expandedTypes.includes(group.type) && group.accounts.length === 0 && (
                  <div className="p-6 text-center text-muted-foreground border-t border-border">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No {group.config.label.toLowerCase()} accounts found</p>
                    <button
                      onClick={() => { setEditAccount(null); setShowModal(true); }}
                      className="mt-2 text-sm text-primary hover:underline"
                    >
                      + Add {group.config.label.toLowerCase()} account
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New Account Modal - only mounted while open, so every open computes
          a fresh auto-generated code off the current accounts list instead
          of reusing whatever was loaded (often still empty) the first time
          this page rendered. key still resets state when switching between
          editing different accounts within a single open modal. */}
      {showModal && (
        <NewAccountModal
          key={editAccount?.id || 'new'}
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditAccount(null); }}
          accounts={accounts}
          companyId={selectedCompany?.id}
          editAccount={editAccount}
        />
      )}
    </div>
  );
}
